import _toConsumableArray from "/builder/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import { Slice, Fragment } from 'prosemirror-model';
import { Plugin, PluginKey } from 'prosemirror-state';
import { ExtensionPriority } from '@remirror/core-constants';
import { sort, includes, findMatches, isFunction, range } from '@remirror/core-helpers';
/**
 * Create the paste plugin handler.
 */

function pasteRules(pasteRules) {
  var sortedPasteRules = sort(pasteRules, function (a, z) {
    var _z$priority, _a$priority;

    return ((_z$priority = z.priority) !== null && _z$priority !== void 0 ? _z$priority : ExtensionPriority.Low) - ((_a$priority = a.priority) !== null && _a$priority !== void 0 ? _a$priority : ExtensionPriority.Low);
  }); // Container for the regex based paste rules.

  var regexPasteRules = []; // Container for the file based paste rules.

  var filePasteRules = [];

  var _iterator = _createForOfIteratorHelper(sortedPasteRules),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var rule = _step.value;

      if (isRegexPastRule(rule)) {
        regexPasteRules.push(rule);
      } else {
        filePasteRules.push(rule);
      }
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  var _view;

  return new Plugin({
    key: pastePluginKey,
    view: function view(editorView) {
      _view = editorView;
      return {};
    },
    props: {
      // The regex based paste rules are passed into this function to take care of.
      transformPasted: function transformPasted(slice) {
        var $pos = _view.state.selection.$from;
        var nodeName = $pos.node().type.name;
        var markNames = new Set($pos.marks().map(function (mark) {
          return mark.type.name;
        })); // Iterate over each rule by order of priority and update the slice each time.

        var _iterator2 = _createForOfIteratorHelper(regexPasteRules),
            _step2;

        try {
          for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
            var _rule$ignoredNodes, _rule$ignoredMarks, _slice$content$firstC, _slice$content$firstC2;

            var rule = _step2.value;

            if ( // The parent node is ignored.
            (_rule$ignoredNodes = rule.ignoredNodes) !== null && _rule$ignoredNodes !== void 0 && _rule$ignoredNodes.includes(nodeName) || // The current position contains ignored marks.
            (_rule$ignoredMarks = rule.ignoredMarks) !== null && _rule$ignoredMarks !== void 0 && _rule$ignoredMarks.some(function (ignored) {
              return markNames.has(ignored);
            })) {
              continue;
            }

            var textContent = (_slice$content$firstC = (_slice$content$firstC2 = slice.content.firstChild) === null || _slice$content$firstC2 === void 0 ? void 0 : _slice$content$firstC2.textContent) !== null && _slice$content$firstC !== void 0 ? _slice$content$firstC : '';
            var canBeReplaced = !_view.state.selection.empty && slice.content.childCount === 1 && textContent;
            var _match = findMatches(textContent, rule.regexp)[0];

            if (canBeReplaced && _match && rule.type === 'mark' && rule.replaceSelection) {
              var _view$state$selection = _view.state.selection,
                  from = _view$state$selection.from,
                  to = _view$state$selection.to;

              var textSlice = _view.state.doc.slice(from, to);

              var textNode = textSlice.content.firstChild;

              if (textNode !== null && textNode !== void 0 && textNode.isText && (typeof rule.replaceSelection !== 'boolean' ? rule.replaceSelection(textNode.textContent) : rule.replaceSelection)) {
                var getAttributes = rule.getAttributes,
                    markType = rule.markType;
                var attributes = isFunction(getAttributes) ? getAttributes(_match, true) : getAttributes; // const nodeWithMark = textNode.mark([markType.create(attributes), ...textNode.marks]);
                // return new Slice(nodeWithMark.content, slice.openStart, slice.openEnd);

                return new Slice(Fragment.fromArray([textNode.mark([markType.create(attributes)].concat(_toConsumableArray(textNode.marks)))]), slice.openStart, slice.openEnd);
              }
            }

            slice = new Slice(regexPasteRuleHandler(slice.content, rule, _view.state.schema), slice.openStart, slice.openEnd);
          }
        } catch (err) {
          _iterator2.e(err);
        } finally {
          _iterator2.f();
        }

        return slice;
      },
      handleDOMEvents: {
        // Handle paste for pasting content.
        paste: function paste(view, event) {
          var _view$props$editable, _view$props;

          if (!((_view$props$editable = (_view$props = view.props).editable) !== null && _view$props$editable !== void 0 && _view$props$editable.call(_view$props, view.state))) {
            return false;
          }

          var clipboardData = event.clipboardData;

          if (!clipboardData) {
            return false;
          }

          var allFiles = _toConsumableArray(clipboardData.items).map(function (data) {
            return data.getAsFile();
          }).filter(function (file) {
            return !!file;
          });

          if (allFiles.length === 0) {
            return false;
          }

          var selection = view.state.selection;

          var _iterator3 = _createForOfIteratorHelper(filePasteRules),
              _step3;

          try {
            var _loop = function _loop() {
              var _step3$value = _step3.value,
                  fileHandler = _step3$value.fileHandler,
                  regexp = _step3$value.regexp;
              var files = regexp ? allFiles.filter(function (file) {
                return regexp.test(file.type);
              }) : allFiles;

              if (files.length === 0) {
                return "continue";
              }

              if (fileHandler({
                event: event,
                files: files,
                selection: selection,
                view: view,
                type: 'paste'
              })) {
                event.preventDefault();
                return {
                  v: true
                };
              }
            };

            for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
              var _ret = _loop();

              if (_ret === "continue") continue;
              if (typeof _ret === "object") return _ret.v;
            }
          } catch (err) {
            _iterator3.e(err);
          } finally {
            _iterator3.f();
          }

          return false;
        },
        // Handle drop for pasting content.
        drop: function drop(view, event) {
          var _view$props$editable2, _view$props2, _view$posAtCoords$pos, _view$posAtCoords;

          if (!((_view$props$editable2 = (_view$props2 = view.props).editable) !== null && _view$props$editable2 !== void 0 && _view$props$editable2.call(_view$props2, view.state))) {
            return false;
          }

          var dataTransfer = event.dataTransfer,
              clientX = event.clientX,
              clientY = event.clientY;

          if (!dataTransfer) {
            return false;
          }

          var allFiles = getDataTransferFiles(event);

          if (allFiles.length === 0) {
            return false;
          }

          var pos = (_view$posAtCoords$pos = (_view$posAtCoords = view.posAtCoords({
            left: clientX,
            top: clientY
          })) === null || _view$posAtCoords === void 0 ? void 0 : _view$posAtCoords.pos) !== null && _view$posAtCoords$pos !== void 0 ? _view$posAtCoords$pos : view.state.selection.anchor;

          var _iterator4 = _createForOfIteratorHelper(filePasteRules),
              _step4;

          try {
            var _loop2 = function _loop2() {
              var _step4$value = _step4.value,
                  fileHandler = _step4$value.fileHandler,
                  regexp = _step4$value.regexp;
              var files = regexp ? allFiles.filter(function (file) {
                return regexp.test(file.type);
              }) : allFiles;

              if (files.length === 0) {
                return "continue";
              }

              if (fileHandler({
                event: event,
                files: files,
                pos: pos,
                view: view,
                type: 'drop'
              })) {
                event.preventDefault();
                return {
                  v: true
                };
              }
            };

            for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
              var _ret2 = _loop2();

              if (_ret2 === "continue") continue;
              if (typeof _ret2 === "object") return _ret2.v;
            }
          } catch (err) {
            _iterator4.e(err);
          } finally {
            _iterator4.f();
          }

          return false;
        }
      }
    }
  });
}

var pastePluginKey = new PluginKey('pasteRule');
/**
 * @template RegexPasteRule
 */

/**
 * Factory for creating paste rules.
 */

function createPasteRuleHandler(transformer, schema) {
  return function handler(props) {
    var fragment = props.fragment,
        rule = props.rule,
        nodes = props.nodes;
    var regexp = rule.regexp,
        ignoreWhitespace = rule.ignoreWhitespace,
        ignoredMarks = rule.ignoredMarks,
        ignoredNodes = rule.ignoredNodes;
    fragment.forEach(function (child) {
      var _child$text; // Check if this node should be ignored.


      if (ignoredNodes !== null && ignoredNodes !== void 0 && ignoredNodes.includes(child.type.name) || isCodeNode(child)) {
        nodes.push(child);
        return;
      } // When the current node is not a text node, recursively dive into it's child nodes.


      if (!child.isText) {
        nodes.push(child.copy(handler({
          fragment: child.content,
          rule: rule,
          nodes: []
        })));
        return;
      } // When this is a text node ignore this child if it is wrapped by an ignored
      // mark or a code mark.


      if (child.marks.some(function (mark) {
        return isCodeMark(mark) || (ignoredMarks === null || ignoredMarks === void 0 ? void 0 : ignoredMarks.includes(mark.type.name));
      })) {
        nodes.push(child);
        return;
      }

      var text = (_child$text = child.text) !== null && _child$text !== void 0 ? _child$text : '';
      var pos = 0; // Find all matches and add the defined mark.

      var _iterator5 = _createForOfIteratorHelper(findMatches(text, regexp)),
          _step5;

      try {
        for (_iterator5.s(); !(_step5 = _iterator5.n()).done;) {
          var _match2 = _step5.value; // The captured value from the regex.

          var capturedValue = _match2[1];
          var fullValue = _match2[0];

          if ( // This helps prevent matches which are only whitespace from triggering
          // an update.
          ignoreWhitespace && (capturedValue === null || capturedValue === void 0 ? void 0 : capturedValue.trim()) === '' || !fullValue) {
            return;
          }

          var start = _match2.index;
          var end = start + fullValue.length;

          if (start > 0) {
            nodes.push(child.cut(pos, start));
          }

          var textNode = child.cut(start, end); // When a capture value is provided use it.

          if (fullValue && capturedValue) {
            var startSpaces = fullValue.search(/\S/);
            var textStart = start + fullValue.indexOf(capturedValue);
            var textEnd = textStart + capturedValue.length;

            if (startSpaces) {
              nodes.push(child.cut(start, start + startSpaces));
            }

            textNode = child.cut(textStart, textEnd);
          } // A transformer to push the required nodes.


          transformer({
            nodes: nodes,
            rule: rule,
            textNode: textNode,
            match: _match2,
            schema: schema
          });
          pos = end;
        } // Add the rest of the node to the gathered nodes if any characters are
        // remaining.

      } catch (err) {
        _iterator5.e(err);
      } finally {
        _iterator5.f();
      }

      if (text && pos < text.length) {
        nodes.push(child.cut(pos));
      }
    });
    return Fragment.fromArray(nodes);
  };
}
/**
 * Mark rule transformer which pushes the transformed mark into the provided
 * nodes.
 */


function markRuleTransformer(props) {
  var _ref;

  var nodes = props.nodes,
      rule = props.rule,
      textNode = props.textNode,
      match = props.match,
      schema = props.schema;
  var transformMatch = rule.transformMatch,
      getAttributes = rule.getAttributes,
      markType = rule.markType;
  var attributes = isFunction(getAttributes) ? getAttributes(match, false) : getAttributes;
  var transformedCapturedValue = transformMatch === null || transformMatch === void 0 ? void 0 : transformMatch(match); // remove the text if transformMatch return

  if (transformedCapturedValue === '') {
    return;
  }

  var text = (_ref = transformedCapturedValue !== null && transformedCapturedValue !== void 0 ? transformedCapturedValue : textNode.text) !== null && _ref !== void 0 ? _ref : '';
  var marks = [markType.create(attributes)].concat(_toConsumableArray(textNode.marks));
  nodes.push(schema.text(text, marks));
}
/**
 * Support for pasting and transforming text content into the editor.
 */


function textRuleTransformer(props) {
  var _ref2;

  var nodes = props.nodes,
      rule = props.rule,
      textNode = props.textNode,
      match = props.match,
      schema = props.schema;
  var transformMatch = rule.transformMatch;
  var transformedCapturedValue = transformMatch === null || transformMatch === void 0 ? void 0 : transformMatch(match); // remove the text if transformMatch return

  if (transformedCapturedValue === '') {
    return;
  }

  var text = (_ref2 = transformedCapturedValue !== null && transformedCapturedValue !== void 0 ? transformedCapturedValue : textNode.text) !== null && _ref2 !== void 0 ? _ref2 : '';
  nodes.push(schema.text(text, textNode.marks));
}
/**
 * Support for pasting node content into the editor.
 */


function nodeRuleTransformer(props) {
  var nodes = props.nodes,
      rule = props.rule,
      textNode = props.textNode,
      match = props.match;
  var getAttributes = rule.getAttributes,
      nodeType = rule.nodeType,
      getContent = rule.getContent;
  var attributes = isFunction(getAttributes) ? getAttributes(match, false) : getAttributes;
  var content = (getContent ? getContent(match) : textNode) || undefined;
  nodes.push(nodeType.createChecked(attributes, content));
}
/**
 * The run the handlers for the regex paste rules on the content which has been transformed by prosemirror.
 */


function regexPasteRuleHandler(fragment, rule, schema) {
  var nodes = [];

  switch (rule.type) {
    case 'mark':
      return createPasteRuleHandler(markRuleTransformer, schema)({
        fragment: fragment,
        nodes: nodes,
        rule: rule
      });

    case 'node':
      return createPasteRuleHandler(nodeRuleTransformer, schema)({
        fragment: fragment,
        nodes: nodes,
        rule: rule
      });

    default:
      return createPasteRuleHandler(textRuleTransformer, schema)({
        fragment: fragment,
        nodes: nodes,
        rule: rule
      });
  }
}

var regexPasteRules = ['mark', 'node', 'text'];
/**
 * Check if the paste rule is regex based.
 */

function isRegexPastRule(rule) {
  return includes(regexPasteRules, rule.type);
}
/**
 * Check whether the current selection is completely contained within a code block or mark.
 */


function isInCode(selection) {
  var _ref3 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref3$contained = _ref3.contained,
      contained = _ref3$contained === void 0 ? true : _ref3$contained;

  if (selection.empty) {
    return resolvedPosInCode(selection.$head);
  }

  if (contained) {
    return resolvedPosInCode(selection.$head) && resolvedPosInCode(selection.$anchor);
  }

  return resolvedPosInCode(selection.$head) || resolvedPosInCode(selection.$anchor);
}
/**
 * Check if the provided position is within a code mark or node.
 */


function resolvedPosInCode($pos) {
  // Start at the current depth and work down until a depth of 1.
  var _iterator6 = _createForOfIteratorHelper(range($pos.depth, 1)),
      _step6;

  try {
    for (_iterator6.s(); !(_step6 = _iterator6.n()).done;) {
      var depth = _step6.value;

      if (isCodeNode($pos.node(depth))) {
        return true;
      }
    }
  } catch (err) {
    _iterator6.e(err);
  } finally {
    _iterator6.f();
  }

  var _iterator7 = _createForOfIteratorHelper($pos.marks()),
      _step7;

  try {
    for (_iterator7.s(); !(_step7 = _iterator7.n()).done;) {
      var mark = _step7.value;

      if (isCodeMark(mark)) {
        return true;
      }
    }
  } catch (err) {
    _iterator7.e(err);
  } finally {
    _iterator7.f();
  }

  return false;
}
/**
 * Check if the current node is a code node.
 */


function isCodeNode(node) {
  var _node$type$spec$group;

  return node.type.spec.code || ((_node$type$spec$group = node.type.spec.group) === null || _node$type$spec$group === void 0 ? void 0 : _node$type$spec$group.split(' ').includes('code'));
}
/**
 * Check if the current mark is a code mark.
 */


function isCodeMark(mark) {
  var _mark$type$spec$group;

  return mark.type.name === 'code' || ((_mark$type$spec$group = mark.type.spec.group) === null || _mark$type$spec$group === void 0 ? void 0 : _mark$type$spec$group.split(' ').includes('code'));
}

function getDataTransferFiles(event) {
  var _dataTransfer$files, _dataTransfer$items;

  var dataTransfer = event.dataTransfer;

  if (!dataTransfer) {
    return [];
  }

  if (((_dataTransfer$files = dataTransfer.files) === null || _dataTransfer$files === void 0 ? void 0 : _dataTransfer$files.length) > 0) {
    return _toConsumableArray(dataTransfer.files);
  }

  if ((_dataTransfer$items = dataTransfer.items) !== null && _dataTransfer$items !== void 0 && _dataTransfer$items.length) {
    // During the drag even the dataTransfer.files is null
    // but Chrome implements some drag store, which is accesible via dataTransfer.items
    return _toConsumableArray(dataTransfer.items).map(function (item) {
      return item.getAsFile();
    }).filter(function (item) {
      return !!item;
    });
  }

  return [];
}

export { isInCode, pasteRules };